import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

interface PricingTier {
  key: string;
  title: string;
  subtitle: string;
  priceText: string;
  subpriceText?: string;
  cta: string;
  features: string[];
  popular?: boolean;
  priceValue: number; // price in dollars for logic
}

interface Props {
  filteredOptions: {
    key: string;
    label: string;
  }[];
  calculateChargeAmount: (key: string) => number;
  selectedOption: string | null;
  handleSelectOption: (key: string) => void;
}

const tierDetailsMap: Record<
  string,
  Omit<PricingTier, "key" | "priceValue">
> = {
  smsTool: {
    title: "Text-to-Inventory Assistant*",
    subtitle:
      "Movers that want to guide the customer through the image process with auto inventory capture.",
    priceText: "$149 per month",
    subpriceText: "* $0.40 per image billed monthly",
    cta: "LET'S GO",
    features: [
      "AI-powered inventory capture tool",
      "Basic self-serve booking flow",
      "Email support",
      "Simple lead capture form integration",
      "Daily inventory submissions",
      "White-labeled experience",
      "CRM integration for lead flow",
    ],
  },
  bookingTool: {
    title: "AutoBook + Support Bot*",
    subtitle:
      "An agentic customer support bot and full-service booking tool - 24/7.",
    priceText: "$499 per month",
    subpriceText: "* $0.35 per image billed monthly",
    cta: "LET'S GO",
    features: [
      "All Starter features, plus:",
      "Sales-assisted survey tool",
      "Priority email & phone support",
      "Website-embeddable inventory tool",
      "Enhanced CRM integrations",
    ],
  },
  combo: {
    title: "WeMovePro SmartSuite*",
    subtitle:
      "Best approach to allow your sales rep to succeed and you as a moving company to succeed simultaneously.",
    priceText: "$599 per month",
    subpriceText: "* $0.30 per image billed monthly",
    cta: "LET'S GO",
    features: [
      "Fully customizable chatbot UI & messaging",
      "Intelligent FAQ & knowledge base management",
      "AI-driven response tuning for accuracy",
      "Embedded chatbot on your site",
      "CRM sync for chat leads and transcripts",
      "Smart lead qualification & routing",
    ],
    popular: true,
  },
};

const PricingOptions: React.FC<Props> = ({
  filteredOptions,
  calculateChargeAmount,
  selectedOption,
  handleSelectOption,
}) => {
  const theme = useTheme();

  return (
    <Grid container spacing={2} justifyContent="center">
      {filteredOptions.map(({ key }) => {
        const isSelected = selectedOption === key;
        const tierDetails = tierDetailsMap[key];
        const price = calculateChargeAmount(key);

        return (
          <Grid item xs={12} sm={6} md={4} key={key}>
            <Paper
              elevation={3}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                backgroundColor: "#ffffff",
                color: "#000000",
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
                border: "1px solid #e0e0e0",
                boxShadow: tierDetails.popular
                  ? "0 8px 24px rgba(0,0,0,0.12)"
                  : "0 2px 8px rgba(0,0,0,0.1)",
                cursor: "pointer",
                userSelect: "none",
                ...(isSelected && {
                  borderColor: "#1976d2",
                  boxShadow: "0 0 12px rgba(25, 118, 210, 0.6)",
                }),
                p: 1, // reduced padding overall
              }}
              onClick={() => handleSelectOption(key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelectOption(key);
                }
              }}
              aria-pressed={isSelected}
            >
              {/* Popular Badge */}
              {tierDetails.popular && (
                <Box
                  sx={{
                    backgroundColor: "#4285f4",
                    color: "white",
                    px: 1.5,
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                    minHeight: "1.8rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    top: 0,
                    right: 0,
                    borderBottomLeftRadius: 8,
                    zIndex: 10,
                  }}
                >
                  MOST POPULAR
                </Box>
              )}

              {/* Left Side: Title + Subtitle */}
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  pt: tierDetails.popular ? 3 : 2,
                  display: "flex",
                  flexDirection: "column",
                  // justifyContent: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    whiteSpace: "pre-line",
                    lineHeight: 1.2,
                    mb: 1,
                    fontSize: "0.9rem",
                  }}
                >
                  {tierDetails.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(0, 0, 0, 0.7)",
                    whiteSpace: "normal",
                    fontSize: "0.75rem",
                  }}
                >
                  {tierDetails.subtitle}
                </Typography>
              </Box>

              {/* Right Side: Price, Button, Features */}
              <Box
                sx={{
                  width: { xs: "100%", md: 280 },
                  borderLeft: { xs: "none", md: "1px solid #e0e0e0" },
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      fontWeight: "bold",
                      // mb: 0.3,
                      fontSize: "1.2rem",
                    }}
                  >
                    ${price} per month
                  </Typography>
                  {tierDetails.subpriceText && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontStyle: "italic",
                        color: "rgba(0, 0, 0, 0.6)",
                        fontSize: "0.7rem",
                      }}
                    >
                      {tierDetails.subpriceText}
                    </Typography>
                  )}
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#4285f4",
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    textTransform: "none",
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#3367d6",
                    },
                  }}
                >
                  {tierDetails.cta}
                </Button>

                <Divider sx={{ mb: 1 }} />

                <Box
                  sx={{
                    maxHeight: 140,
                    overflowY: "auto",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      fontSize: "0.85rem",
                    }}
                  >
                    Features include:
                  </Typography>

                  <List dense sx={{ mb: 0 }}>
                    {tierDetails.features.map((feature, i) => (
                      <ListItem key={i} sx={{ py: 0.3, pl: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckIcon
                            sx={{ color: "#4caf50" }}
                            fontSize="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { lineHeight: 1.2, fontSize: "0.75rem" },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PricingOptions;
